import StarterKit from '@tiptap/starter-kit';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { createLowlight, common } from 'lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Link } from '@tiptap/extension-link';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { useTranslation } from 'react-i18next';
import type { Extensions } from '@tiptap/core';
import { buildSlashCommandsExtension } from '../extensions/slash-commands';
import type { SlashMenuStore } from '../components/slash-menu-store';
import { buildDragHandleExtension } from '../extensions/drag-handle';
import { buildMentionExtension } from '../extensions/mention';
import { buildCalloutExtension } from '../extensions/callout';
import { buildExpandableExtension } from '../extensions/expandable';
import { buildImageUploadExtension } from '../extensions/image-upload';
import { buildVideoEmbedExtension } from '../extensions/video-embed';
import type { RichTextEditorProps } from '../RichTextEditor.types';

type ExtensionConfig = Pick<
  RichTextEditorProps,
  | 'placeholder'
  | 'enableSlashCommands'
  | 'enableDragHandle'
  | 'enableMentions'
  | 'enableImages'
  | 'enableCallouts'
  | 'enableExpandable'
  | 'enableCodeHighlighting'
  | 'enableVideoEmbed'
  | 'extensions'
  | 'mentionFetchUsers'
> & {
  /** Bridge for the slash menu rendered by SlashMenuHost inside the editor subtree. */
  slashMenuStore?: SlashMenuStore;
};

const lowlight = createLowlight(common);

/** Builds the TipTap extensions array from editor props. Centralizes all extension configuration. */
export function useEditorExtensions({
  placeholder,
  enableSlashCommands = true,
  enableDragHandle = true,
  enableMentions = false,
  enableCallouts = true,
  enableExpandable = true,
  enableCodeHighlighting = true,
  enableImages = false,
  enableVideoEmbed = false,
  extensions: extraExtensions = [],
  mentionFetchUsers,
  slashMenuStore,
}: ExtensionConfig): Extensions {
  const { t } = useTranslation('richText');

  return [
    StarterKit.configure({
      codeBlock: enableCodeHighlighting ? false : undefined,
    }),
    ...(enableCodeHighlighting ? [CodeBlockLowlight.configure({ lowlight })] : []),
    Placeholder.configure({
      placeholder: placeholder ?? '',
      emptyNodeClass: 'is-empty',
    }),
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
        target: null,
      },
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Underline,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    Subscript,
    Superscript,
    ...(enableCallouts ? [buildCalloutExtension()] : []),
    ...(enableExpandable ? buildExpandableExtension() : []),
    ...(enableImages ? [buildImageUploadExtension()] : []),
    ...(enableVideoEmbed ? [buildVideoEmbedExtension()] : []),
    ...(enableSlashCommands ? [buildSlashCommandsExtension(t, slashMenuStore)] : []),
    ...(enableDragHandle ? [buildDragHandleExtension()] : []),
    ...(enableMentions ? [buildMentionExtension(mentionFetchUsers ?? (async () => []))] : []),
    ...extraExtensions,
  ];
}
